"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ViewConfigRowInvalidError = exports.VIEW_CONFIG_COLUMN_DEFS = exports.ViewConfigRowSchema = exports.ContentDbRefSchema = exports.EntryStyleSchema = exports.PaginationModeSchema = exports.NavModeSchema = exports.RowDetailModeSchema = exports.LayoutTypeSchema = void 0;
exports.getViewConfigColumnId = getViewConfigColumnId;
exports.parseRow = parseRow;
const database_identity_1 = require("@streamlain/database-identity");
const zod_1 = require("zod");
const app_base_config_1 = require("./app-base-config");
const formula_1 = require("./formula");
exports.LayoutTypeSchema = zod_1.z.enum(['list', 'grid', 'kanban', 'dashboard']);
exports.RowDetailModeSchema = zod_1.z.enum(['none', 'page', 'modal']);
exports.NavModeSchema = zod_1.z.enum(['none', 'top', 'sidebar', 'both']);
exports.PaginationModeSchema = zod_1.z.enum(['none', 'numbered', 'infinite']);
exports.EntryStyleSchema = zod_1.z.enum([
    'plain',
    'card',
    'image-card',
    'hero-card',
]);
exports.ContentDbRefSchema = zod_1.z
    .object({
    databaseId: zod_1.z.string().min(1),
    docId: zod_1.z.string().min(1),
    blockId: zod_1.z.string().min(1),
    contentModel: app_base_config_1.AppBaseContentModelSchema,
})
    .strict();
const OptionalStringStorageSchema = zod_1.z.preprocess(value => (value === null ? undefined : value), zod_1.z.string().optional());
exports.ViewConfigRowSchema = zod_1.z.object({
    viewId: zod_1.z.string().min(1),
    name: zod_1.z.string(),
    slugPrefix: OptionalStringStorageSchema,
    layoutType: exports.LayoutTypeSchema,
    rowDetailMode: exports.RowDetailModeSchema,
    inlineEdit: zod_1.z.boolean(),
    nav: exports.NavModeSchema,
    search: zod_1.z.boolean(),
    pagination: exports.PaginationModeSchema,
    hero: zod_1.z.boolean(),
    entryStyle: exports.EntryStyleSchema,
    tags: zod_1.z.boolean(),
    form: zod_1.z.boolean(),
    seoSlugCol: zod_1.z.string().nullable(),
    seoMetaCol: zod_1.z.string().nullable(),
    seoOgCol: zod_1.z.string().nullable(),
    seoCanonicalCol: zod_1.z.string().nullable(),
    seoRobotsIndex: zod_1.z.boolean(),
    seoTitleFormula: formula_1.FormulaSchema.nullable().optional(),
    seoDescriptionFormula: formula_1.FormulaSchema.nullable().optional(),
    seoOgImageFormula: formula_1.FormulaSchema.nullable().optional(),
    contentDbRef: exports.ContentDbRefSchema.nullable(),
    appBaseConfig: app_base_config_1.AppBaseConfigSchema.optional(),
    // sl-ya4v.4b-E (Cluster E removal): per-view `domain` field deleted.
    // Routing target now lives at the routing entity: `custom_domains.app_base_id`
    // FK (migration 270). Service-side validation in CustomDomainService ensures
    // the bound view belongs to the same workspace.
    themeRef: zod_1.z.string().nullable(),
    bindings: zod_1.z.record(zod_1.z.string(), zod_1.z.string()),
    isDefault: zod_1.z.boolean(),
});
function unwrapColumnZodType(zodType) {
    return zodType instanceof zod_1.z.ZodNullable || zodType instanceof zod_1.z.ZodOptional
        ? zodType.unwrap()
        : zodType;
}
function deriveBlockSuiteType(zodType) {
    const t = unwrapColumnZodType(zodType);
    if (t instanceof zod_1.z.ZodBoolean)
        return 'checkbox';
    if (t instanceof zod_1.z.ZodEnum)
        return 'select';
    if (t instanceof zod_1.z.ZodNumber)
        return 'number';
    return 'rich-text';
}
/**
 * A select column of a schema-locked table accepts only its options, so an
 * enum field carries its values as options; a stored cell holds the value.
 */
function deriveColumnData(zodType) {
    const t = unwrapColumnZodType(zodType);
    if (!(t instanceof zod_1.z.ZodEnum))
        return {};
    return {
        options: t.options.map(value => ({
            id: value,
            value,
            color: database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Grey,
        })),
    };
}
/** Fields of a config row that are columns; the settings live in the Config tables. */
exports.VIEW_CONFIG_COLUMN_DEFS = (() => {
    const shape = exports.ViewConfigRowSchema.shape;
    return Object.keys(shape)
        .filter(field => field !== 'appBaseConfig')
        .map(field => ({
        field,
        zodType: shape[field],
        blockSuiteType: deriveBlockSuiteType(shape[field]),
        data: deriveColumnData(shape[field]),
    }));
})();
function getViewConfigColumnId(field) {
    return `view-config:${String(field)}`;
}
/**
 * A persisted view-config row that no longer matches the schema. Stays a
 * ZodError (issues with field paths) but reads as one sentence naming the
 * offending fields and the repair, instead of the raw issue JSON.
 */
/** A stored config row that cannot be read, with the issues that make it unreadable. */
class ViewConfigRowInvalidError extends Error {
    constructor(issues) {
        super(viewConfigRowInvalidMessage(issues));
        this.issues = issues;
        this.name = 'ViewConfigRowInvalidError';
    }
}
exports.ViewConfigRowInvalidError = ViewConfigRowInvalidError;
function viewConfigRowInvalidMessage(issues) {
    const repair = 'Rebind the affected database or restore a valid app version.';
    const [first, ...rest] = issues;
    if (!first)
        return `Stored app configuration cannot be read. ${repair}`;
    const location = first.path.map(String).join('.') || 'row';
    const more = rest.length > 0 ? ` (+${rest.length} more)` : '';
    return `Stored app configuration cannot be read: ${location}: ${first.message}${more}. ${repair}`;
}
function parseRow(raw) {
    if (raw && typeof raw === 'object') {
        (0, app_base_config_1.validateNoDeprecatedBlobFields)(raw['appBaseConfig']);
    }
    const parsed = exports.ViewConfigRowSchema.safeParse(raw);
    if (!parsed.success)
        throw new ViewConfigRowInvalidError(parsed.error.issues);
    return parsed.data;
}
